import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Plus, Search, Trash2, Edit3, X, Loader2, Instagram, Music2, Youtube, Linkedin, Twitter, Globe, Users, Download } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  prospect: { label: "Prospect", color: "#3B82F6" },
  contacted: { label: "Contacté", color: "#F59E0B" },
  negotiating: { label: "En négo", color: "#7C3AED" },
  partner: { label: "Partenaire", color: "#10B981" },
  archived: { label: "Archivé", color: "#9CA3AF" },
};

const PLATFORM_ICONS = {
  instagram: Instagram, tiktok: Music2, youtube: Youtube, x: Twitter, linkedin: Linkedin, other: Globe,
};

export default function CRMContacts() {
  const [contacts, setContacts] = useState([]);
  const [segments, setSegments] = useState([]);
  const [search, setSearch] = useState("");
  const [fPlatform, setFPlatform] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [view, setView] = useState("table");
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    try {
      const [c, s] = await Promise.all([api.crmListContacts(), api.crmListSegments()]);
      setContacts(c); setSegments(s);
    } catch { toast.error("Erreur chargement"); }
  };
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => contacts.filter(c => {
    if (fPlatform !== "all" && c.platform !== fPlatform) return false;
    if (fStatus !== "all" && c.status !== fStatus) return false;
    if (search && !`${c.name} ${c.handle} ${c.niche}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [contacts, fPlatform, fStatus, search]);

  const remove = async (id) => {
    if (!confirm("Supprimer ce contact ?")) return;
    await api.crmDeleteContact(id); refresh();
  };

  const exportCSV = () => {
    const headers = ["Name", "Handle", "Platform", "Niche", "Followers", "Engagement", "Status", "Email"];
    const rows = filtered.map(c => [c.name, c.handle, c.platform, c.niche, c.follower_count, c.engagement_rate, c.status, c.email].map(v => `"${v ?? ""}"`).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `contacts-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 animate-fadeUp" data-testid="crm-contacts-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--brand-pink)" }}>CRM Social</div>
          <h1 className="font-display text-4xl font-bold tracking-tight" style={{ color: "var(--text-primary)" }}>Contacts & Leads</h1>
          <p className="text-sm mt-1.5" style={{ color: "var(--text-muted)" }}>Tous tes profils suivis, classés et filtrables.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCSV} data-testid="export-csv-btn"
            className="text-sm font-semibold px-4 py-2.5 rounded-xl border flex items-center gap-2 transition"
            style={{ borderColor: "var(--border-default)", color: "var(--text-secondary)" }}>
            <Download className="w-4 h-4" /> CSV
          </button>
          <button onClick={() => setShowAdd(true)} data-testid="add-contact-btn"
            className="cdo-gradient text-white text-sm font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2 glow-purple">
            <Plus className="w-4 h-4" /> Nouveau contact
          </button>
        </div>
      </header>

      <div className="surface p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--text-muted)" }} />
          <input data-testid="contact-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher (nom, @handle, niche)..."
            className="w-full pl-10 pr-3 py-2 rounded-lg text-sm outline-none"
            style={{ background: "var(--bg-base)", border: "1px solid var(--border-default)", color: "var(--text-primary)" }} />
        </div>
        <Select v={fPlatform} on={setFPlatform} testid="f-platform" opts={[["all", "Toutes plateformes"], ["instagram", "Instagram"], ["tiktok", "TikTok"], ["youtube", "YouTube"], ["x", "X"], ["linkedin", "LinkedIn"], ["other", "Autre"]]} />
        <Select v={fStatus} on={setFStatus} testid="f-status" opts={[["all", "Tous statuts"], ...Object.entries(STATUS_META).map(([k, m]) => [k, m.label])]} />
        <div className="flex rounded-lg border" style={{ borderColor: "var(--border-default)" }}>
          <button onClick={() => setView("table")} className="px-3 py-1.5 text-xs font-semibold" style={{ background: view === "table" ? "var(--brand-purple)" : "transparent", color: view === "table" ? "white" : "var(--text-muted)" }}>Table</button>
          <button onClick={() => setView("cards")} className="px-3 py-1.5 text-xs font-semibold" style={{ background: view === "cards" ? "var(--brand-purple)" : "transparent", color: view === "cards" ? "white" : "var(--text-muted)" }}>Cards</button>
        </div>
        <div className="text-xs font-mono ml-auto" style={{ color: "var(--text-muted)" }}>{filtered.length} / {contacts.length}</div>
      </div>

      {filtered.length === 0 ? (
        <div className="surface p-12 text-center">
          <Users className="w-12 h-12 mx-auto mb-3" style={{ color: "var(--text-muted)" }} strokeWidth={1.2} />
          <div style={{ color: "var(--text-muted)" }} className="text-sm">
            {contacts.length === 0 ? "Aucun contact — ajoute ton premier prospect." : "Aucun résultat avec ces filtres."}
          </div>
        </div>
      ) : view === "table" ? (
        <div className="surface overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border-default)", background: "var(--bg-surface-hover)" }}>
                {["Contact", "Plateforme", "Niche", "Abonnés", "Eng.", "Statut", "Dernière interaction", ""].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => {
                const PI = PLATFORM_ICONS[c.platform] || Globe;
                const meta = STATUS_META[c.status] || STATUS_META.prospect;
                return (
                  <tr key={c.id} data-testid={`contact-row-${i}`} className="hover:bg-purple-50 cursor-pointer transition" style={{ borderBottom: "1px solid var(--border-subtle)" }} onClick={() => setEditing(c)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full cdo-gradient flex items-center justify-center text-white text-[10px] font-bold">{c.name.slice(0, 2).toUpperCase()}</div>
                        <div>
                          <div className="font-semibold" style={{ color: "var(--text-primary)" }}>{c.name}</div>
                          <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>@{c.handle}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><PI className="w-4 h-4" style={{ color: "var(--text-secondary)" }} /></td>
                    <td className="px-4 py-3" style={{ color: "var(--text-secondary)" }}>{c.niche || "—"}</td>
                    <td className="px-4 py-3 font-mono" style={{ color: "var(--text-primary)" }}>{(c.follower_count || 0).toLocaleString("fr-FR")}</td>
                    <td className="px-4 py-3 font-mono" style={{ color: "var(--text-secondary)" }}>{c.engagement_rate}%</td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] font-semibold uppercase px-2 py-1 rounded-md" style={{ background: meta.color + "20", color: meta.color }}>{meta.label}</span>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono" style={{ color: "var(--text-muted)" }}>{c.last_interaction ? c.last_interaction.slice(0, 10) : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={e => { e.stopPropagation(); remove(c.id); }} className="text-gray-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c, i) => {
            const PI = PLATFORM_ICONS[c.platform] || Globe;
            const meta = STATUS_META[c.status] || STATUS_META.prospect;
            return (
              <div key={c.id} data-testid={`contact-card-${i}`} onClick={() => setEditing(c)}
                className="surface surface-hover p-4 cursor-pointer">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full cdo-gradient flex items-center justify-center text-white text-xs font-bold">{c.name.slice(0, 2).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate" style={{ color: "var(--text-primary)" }}>{c.name}</div>
                    <div className="text-xs font-mono truncate" style={{ color: "var(--text-muted)" }}>@{c.handle}</div>
                  </div>
                  <PI className="w-4 h-4" style={{ color: "var(--text-secondary)" }} />
                </div>
                <div className="flex items-center justify-between text-xs mb-2">
                  <span style={{ color: "var(--text-muted)" }}>{c.niche || "—"}</span>
                  <span className="font-mono font-bold" style={{ color: "var(--text-primary)" }}>{(c.follower_count || 0).toLocaleString("fr-FR")}</span>
                </div>
                <span className="text-[10px] font-semibold uppercase px-2 py-1 rounded-md" style={{ background: meta.color + "20", color: meta.color }}>{meta.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {(showAdd || editing) && <ContactForm contact={editing} segments={segments} onClose={() => { setShowAdd(false); setEditing(null); }} onSuccess={() => { setShowAdd(false); setEditing(null); refresh(); }} />}
    </div>
  );
}

const Select = ({ v, on, opts, testid }) => (
  <select data-testid={testid} value={v} onChange={e => on(e.target.value)}
    className="px-3 py-2 rounded-lg text-xs font-semibold outline-none"
    style={{ background: "var(--bg-base)", color: "var(--text-primary)", border: "1px solid var(--border-default)" }}>
    {opts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
  </select>
);

function ContactForm({ contact, segments, onClose, onSuccess }) {
  const [f, setF] = useState(contact || {
    name: "", handle: "", platform: "instagram", niche: "", follower_count: 0,
    engagement_rate: 0, status: "prospect", estimated_value: 0, notes: "", email: "", phone: "", country: "",
  });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const data = { ...f, follower_count: Number(f.follower_count), engagement_rate: Number(f.engagement_rate), estimated_value: Number(f.estimated_value) };
      if (contact) await api.crmUpdateContact(contact.id, data);
      else await api.crmCreateContact(data);
      toast.success(contact ? "Mis à jour" : "Contact créé"); onSuccess();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
        className="rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-3" style={{ background: "var(--bg-surface)" }} data-testid="contact-form">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-xl font-bold" style={{ color: "var(--text-primary)" }}>{contact ? "Modifier" : "Nouveau"} contact</div>
          <button type="button" onClick={onClose}><X className="w-5 h-5" style={{ color: "var(--text-muted)" }} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nom" testid="cf-name" required v={f.name} on={v => set("name", v)} />
          <Field label="@handle" testid="cf-handle" v={f.handle} on={v => set("handle", v)} />
          <SelF label="Plateforme" testid="cf-platform" v={f.platform} on={v => set("platform", v)} opts={["instagram", "tiktok", "youtube", "x", "linkedin", "other"]} />
          <SelF label="Statut" testid="cf-status" v={f.status} on={v => set("status", v)} opts={Object.keys(STATUS_META)} />
          <Field label="Niche" testid="cf-niche" v={f.niche} on={v => set("niche", v)} />
          <Field label="Pays" v={f.country} on={v => set("country", v)} />
          <Field label="Abonnés" type="number" testid="cf-followers" v={f.follower_count} on={v => set("follower_count", v)} />
          <Field label="Engagement %" type="number" step="0.1" v={f.engagement_rate} on={v => set("engagement_rate", v)} />
          <Field label="Valeur estimée (€)" type="number" v={f.estimated_value} on={v => set("estimated_value", v)} />
          <Field label="Email" type="email" v={f.email} on={v => set("email", v)} />
        </div>
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Notes</label>
          <textarea data-testid="cf-notes" rows={3} value={f.notes} onChange={e => set("notes", e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none"
            style={{ background: "var(--bg-base)", color: "var(--text-primary)", border: "1px solid var(--border-default)" }} />
        </div>
        <button disabled={loading} type="submit" data-testid="cf-submit"
          className="w-full cdo-gradient text-white font-semibold py-3 rounded-xl glow-purple disabled:opacity-50 flex items-center justify-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {loading ? "..." : (contact ? "Enregistrer" : "Créer le contact")}
        </button>
      </form>
    </div>
  );
}

const Field = ({ label, v, on, type = "text", required, testid, step }) => (
  <div>
    <label className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</label>
    <input data-testid={testid} type={type} step={step} required={required} value={v ?? ""} onChange={e => on(e.target.value)}
      className="w-full px-3 py-2 rounded-lg text-sm outline-none"
      style={{ background: "var(--bg-base)", color: "var(--text-primary)", border: "1px solid var(--border-default)" }} />
  </div>
);
const SelF = ({ label, v, on, opts, testid }) => (
  <div>
    <label className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</label>
    <select data-testid={testid} value={v} onChange={e => on(e.target.value)}
      className="w-full px-3 py-2 rounded-lg text-sm outline-none"
      style={{ background: "var(--bg-base)", color: "var(--text-primary)", border: "1px solid var(--border-default)" }}>
      {opts.map(o => <option key={o}>{o}</option>)}
    </select>
  </div>
);
